package com.tovtalk.ui.components

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.ChatBubbleOutline
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.Comment
import com.tovtalk.domain.entity.ReplyComment
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.comments.CommentsViewModel
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

private enum class CommentsSortMode { TOP, NEW }

private val MentionRegex = Regex("@\\w+")

@Composable
fun CommentsBottomSheet(
    postId: Int,
    postOwnerId: Int,
    onDismiss: () -> Unit,
    onNavigateToProfile: (Int) -> Unit,
    onNavigateToStories: ((Int) -> Unit)? = null,
    onCommentAdded: () -> Unit = {},
    isGuestMode: Boolean = false,
    onGuestAction: () -> Unit = {},
    currentUserAvatarUrl: String? = null,
    currentUserId: Int = 0,
    viewModel: CommentsViewModel = disposableKoinInject()
) {
    var composerText by remember { mutableStateOf("") }
    val commentsState by viewModel.commentsState.collectAsState()
    val addCommentState by viewModel.addCommentState.collectAsState()
    val replyingTo by viewModel.replyingTo.collectAsState()
    val expandedReplies by viewModel.expandedReplies.collectAsState()
    val repliesMap by viewModel.repliesMap.collectAsState()
    val listState = rememberLazyListState()
    var sortMode by remember { mutableStateOf(CommentsSortMode.TOP) }
    var actionSheetComment by remember { mutableStateOf<Comment?>(null) }

    LaunchedEffect(postId) {
        viewModel.loadComments(postId, refresh = true)
    }

    LaunchedEffect(addCommentState) {
        if (addCommentState is UiState.Success<*>) {
            composerText = ""
            onCommentAdded()
            viewModel.resetAddCommentState()
        }
    }

    val shouldLoadMore by remember {
        derivedStateOf {
            val lastVisibleIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            lastVisibleIndex >= commentsState.items.size - 3 && commentsState.hasMore && !commentsState.isLoadingMore
        }
    }
    LaunchedEffect(shouldLoadMore) {
        if (shouldLoadMore) viewModel.loadMore()
    }

    val sortedComments = remember(commentsState.items, sortMode) {
        val pinned = commentsState.items.filter { it.isPinned }
        val unpinned = commentsState.items.filter { !it.isPinned }
        val sorted = when (sortMode) {
            CommentsSortMode.TOP -> unpinned.sortedWith(
                compareByDescending<Comment> { it.likes }.thenByDescending { it.createdAt.toEpochMilliseconds() }
            )
            CommentsSortMode.NEW -> unpinned.sortedByDescending { it.createdAt.toEpochMilliseconds() }
        }
        pinned + sorted
    }

    // Main overlay
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.overlayDark.copy(alpha = TovTalkTheme.opacity.semiStrong))
            .clickable(onClick = onDismiss),
        contentAlignment = Alignment.BottomCenter
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(enabled = false) {}
        ) {
            val minHeight = maxHeight * 0.5f
            val maxPanelHeight = maxHeight * 0.75f

            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = minHeight, max = maxPanelHeight)
                    .imePadding(),
                shape = TovTalkTheme.shapes.sheet,
                color = TovTalkTheme.colors.surfaceElevated
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    // Header
                    // PERF(6) note: this shows items.size (top-level comments, excludes replies).
                    // PagingState<Comment> exposes no server-provided total field, so there is no
                    // accurate total to use here; leaving items.size to preserve existing behavior.
                    CommentsHeader(
                        count = commentsState.items.size,
                        sortMode = sortMode,
                        onSortModeChange = { sortMode = it },
                        onClose = onDismiss
                    )

                    HorizontalDivider(
                        color = TovTalkTheme.colors.border,
                        thickness = TovTalkTheme.sizes.dividerThickness
                    )

                    // Content
                    when {
                        commentsState.isLoading && sortedComments.isEmpty() -> {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                            }
                        }

                        sortedComments.isEmpty() && !commentsState.isLoading -> {
                            Box(modifier = Modifier.weight(1f)) {
                                EmptyCommentsView()
                            }
                        }

                        else -> {
                            LazyColumn(
                                state = listState,
                                modifier = Modifier.weight(1f),
                                contentPadding = PaddingValues(vertical = 8.dp, horizontal = 6.dp)
                            ) {
                                // PERF(6): Replies were previously rendered with replies.forEach
                                // INSIDE a single LazyColumn item (one slot per parent comment),
                                // which defeats virtualization \u2014 a thread with many replies built
                                // them all eagerly. We now emit each comment, its toggle, and every
                                // reply as their own LazyColumn entries (item/items) with stable keys
                                // so replies are virtualized. Same composables, same callbacks, same
                                // order/behavior \u2014 only the LazyListScope structure changed.
                                sortedComments.forEach { comment ->
                                    item(key = "comment_${comment.id}", contentType = "comment") {
                                        if (comment.isPinned) {
                                            PinnedComment(
                                                comment = comment,
                                                postOwnerId = postOwnerId,
                                                isGuestMode = isGuestMode,
                                                onGuestAction = onGuestAction,
                                                onLikeClick = {
                                                    if (isGuestMode) onGuestAction()
                                                    else viewModel.toggleLikeComment(comment.id, comment.isLiked)
                                                },
                                                onReplyClick = {
                                                    if (isGuestMode) onGuestAction()
                                                    else viewModel.startReply(comment)
                                                },
                                                onUserClick = { onNavigateToProfile(comment.userId) },
                                                onStoryClick = comment.user?.takeIf { it.hasActiveStory }?.let { u -> onNavigateToStories?.let { nav -> { nav(u.id) } } },
                                                onLongPress = { actionSheetComment = comment }
                                            )
                                        } else {
                                            CommentItem(
                                                comment = comment,
                                                postOwnerId = postOwnerId,
                                                onLikeClick = {
                                                    if (isGuestMode) onGuestAction()
                                                    else viewModel.toggleLikeComment(comment.id, comment.isLiked)
                                                },
                                                onReplyClick = {
                                                    if (isGuestMode) onGuestAction()
                                                    else viewModel.startReply(comment)
                                                },
                                                onUserClick = { onNavigateToProfile(comment.userId) },
                                                onStoryClick = comment.user?.takeIf { it.hasActiveStory }?.let { u -> onNavigateToStories?.let { nav -> { nav(u.id) } } },
                                                onLongPress = { actionSheetComment = comment }
                                            )
                                        }
                                    }

                                    // Replies section
                                    if (comment.replyCount > 0) {
                                        val isExpanded = comment.id in expandedReplies
                                        val replies = repliesMap[comment.id] ?: emptyList()

                                        item(key = "replies_toggle_${comment.id}", contentType = "replies_toggle") {
                                            RepliesToggle(
                                                replyCount = comment.replyCount,
                                                isExpanded = isExpanded,
                                                onClick = { viewModel.toggleReplies(comment.id) }
                                            )
                                        }

                                        if (isExpanded) {
                                            items(
                                                items = replies,
                                                key = { reply -> "reply_${reply.id}" },
                                                contentType = { "reply" }
                                            ) { reply ->
                                                ReplyItem(
                                                    reply = reply,
                                                    parentComment = comment,
                                                    onLikeClick = {
                                                        if (isGuestMode) onGuestAction()
                                                        else viewModel.toggleLikeReply(reply.id, comment.id, reply.isLiked)
                                                    },
                                                    onReplyClick = {
                                                        if (isGuestMode) onGuestAction()
                                                        else viewModel.startReply(comment)
                                                    },
                                                    onUserClick = { onNavigateToProfile(reply.userId) },
                                                    onStoryClick = reply.user?.takeIf { it.hasActiveStory }?.let { u -> onNavigateToStories?.let { nav -> { nav(u.id) } } }
                                                )
                                            }
                                            if (replies.size.toLong() < comment.replyCount) {
                                                item(key = "replies_more_${comment.id}", contentType = "replies_more") {
                                                    Surface(
                                                        onClick = { viewModel.loadMoreReplies(comment.id) },
                                                        color = TovTalkTheme.colors.surfaceElevated
                                                    ) {
                                                        Text(
                                                            text = "\u05D4\u05E6\u05D2 \u05E2\u05D5\u05D3 \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA",
                                                            style = TovTalkTheme.typography.caption,
                                                            color = TovTalkTheme.colors.textTertiary,
                                                            modifier = Modifier.padding(start = 58.dp, top = 4.dp, bottom = 4.dp)
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }

                                if (commentsState.isLoadingMore) {
                                    item {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(16.dp),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            CircularProgressIndicator(
                                                modifier = Modifier.size(24.dp),
                                                color = TovTalkTheme.colors.brand
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Reply indicator
                    replyingTo?.let { replyTarget ->
                        ReplyIndicator(
                            username = replyTarget.user?.username ?: "",
                            onCancel = { viewModel.cancelReply() }
                        )
                    }

                    // Input bar
                    CommentInputBar(
                        text = composerText,
                        onTextChange = { composerText = it },
                        onSend = {
                            if (composerText.isNotBlank()) {
                                viewModel.submitComment(composerText.trim())
                                composerText = ""
                            }
                        },
                        onEmojiTap = { emoji ->
                            if (isGuestMode) {
                                onGuestAction()
                            } else {
                                viewModel.submitComment(emoji)
                            }
                        },
                        isLoading = addCommentState is UiState.Loading,
                        isGuestMode = isGuestMode,
                        onGuestAction = onGuestAction,
                        currentUserAvatarUrl = currentUserAvatarUrl
                    )
                }
            }
        }
    }

    // Comment action sheet
    actionSheetComment?.let { comment ->
        CommentActionSheet(
            comment = comment,
            isOwnComment = comment.userId == currentUserId,
            onDismiss = { actionSheetComment = null },
            onDelete = {
                viewModel.deleteComment(comment.id)
                actionSheetComment = null
            }
        )
    }
}

// ─── CommentsHeader ─────────────────────────────────────────────────────────────

@Composable
private fun CommentsHeader(
    count: Int,
    sortMode: CommentsSortMode,
    onSortModeChange: (CommentsSortMode) -> Unit,
    onClose: () -> Unit
) {
    var showSortMenu by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Drag handle
        Box(
            modifier = Modifier
                .padding(top = 8.dp, bottom = 4.dp)
                .width(36.dp)
                .height(4.dp)
                .clip(RoundedCornerShape(2.dp))
                .background(TovTalkTheme.colors.textTertiary)
        )

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = "$count \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA",
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textPrimary
            )

            Row(
                horizontalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xxxs),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box {
                    Surface(
                        onClick = { showSortMenu = true },
                        shape = TovTalkTheme.shapes.medium,
                        color = TovTalkTheme.colors.surfaceOverlay
                    ) {
                        Text(
                            text = if (sortMode == CommentsSortMode.TOP) "\u05DE\u05D5\u05D1\u05D9\u05DC\u05D5\u05EA" else "\u05D7\u05D3\u05E9\u05D5\u05EA",
                            style = TovTalkTheme.typography.labelSmall,
                            color = TovTalkTheme.colors.textPrimary,
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                        )
                    }

                    DropdownMenu(
                        expanded = showSortMenu,
                        onDismissRequest = { showSortMenu = false }
                    ) {
                        DropdownMenuItem(
                            text = { Text("\u05DE\u05D5\u05D1\u05D9\u05DC\u05D5\u05EA") },
                            onClick = {
                                onSortModeChange(CommentsSortMode.TOP)
                                showSortMenu = false
                            }
                        )
                        DropdownMenuItem(
                            text = { Text("\u05D7\u05D3\u05E9\u05D5\u05EA") },
                            onClick = {
                                onSortModeChange(CommentsSortMode.NEW)
                                showSortMenu = false
                            }
                        )
                    }
                }

                IconButton(onClick = onClose) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = StringProvider.get("common_close"),
                        tint = TovTalkTheme.colors.textPrimary
                    )
                }
            }
        }
    }
}

// ─── PinnedComment ──────────────────────────────────────────────────────────────

@Composable
private fun PinnedComment(
    comment: Comment,
    postOwnerId: Int,
    isGuestMode: Boolean,
    onGuestAction: () -> Unit,
    onLikeClick: () -> Unit,
    onReplyClick: () -> Unit,
    onUserClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null,
    onLongPress: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(TovTalkTheme.colors.surfaceOverlay)
            .padding(vertical = 4.dp)
    ) {
        Text(
            text = "\uD83D\uDCCC \u05DE\u05D5\u05E6\u05DE\u05D3\u05EA",
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.textTertiary,
            modifier = Modifier.padding(start = 56.dp, bottom = 2.dp)
        )
        CommentItem(
            comment = comment,
            postOwnerId = postOwnerId,
            onLikeClick = onLikeClick,
            onReplyClick = onReplyClick,
            onUserClick = onUserClick,
            onStoryClick = onStoryClick,
            onLongPress = onLongPress
        )
    }
}

// ─── CommentItem ────────────────────────────────────────────────────────────────

@Composable
private fun CommentItem(
    comment: Comment,
    postOwnerId: Int,
    onLikeClick: () -> Unit,
    onReplyClick: () -> Unit,
    onUserClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null,
    onLongPress: () -> Unit
) {
    val isCreator = comment.userId == postOwnerId

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .pointerInput(Unit) {
                detectTapGestures(
                    onLongPress = { onLongPress() }
                )
            }
            .padding(horizontal = 10.dp, vertical = 6.dp),
        verticalAlignment = Alignment.Top
    ) {
        TovTalkAvatar(
            imageUrl = comment.user?.profilePhoto,
            modifier = Modifier.clickable(onClick = onUserClick),
            size = 32.dp,
            hasStory = (comment.user?.hasActiveStory ?: false) && onStoryClick != null,
            onStoryClick = onStoryClick
        )

        Spacer(modifier = Modifier.width(10.dp))

        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = comment.user?.username ?: StringProvider.get("comments_user"),
                    style = TovTalkTheme.typography.labelMedium,
                    color = TovTalkTheme.colors.textPrimary,
                    modifier = Modifier.clickable(onClick = onUserClick)
                )
                if (isCreator) {
                    Spacer(modifier = Modifier.width(6.dp))
                    CreatorBadge()
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = formatTimeAgo(comment.createdAt),
                    style = TovTalkTheme.typography.caption,
                    color = TovTalkTheme.colors.textTertiary
                )
            }

            Spacer(modifier = Modifier.height(4.dp))

            comment.text?.let {
                Text(
                    text = it,
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textPrimary,
                    maxLines = 8,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.height(6.dp))

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Surface(
                    onClick = onReplyClick,
                    color = TovTalkTheme.colors.surfaceElevated
                ) {
                    Text(
                        text = "\u05D4\u05E9\u05D1",
                        style = TovTalkTheme.typography.caption,
                        color = TovTalkTheme.colors.textTertiary,
                        modifier = Modifier.padding(vertical = 2.dp)
                    )
                }

                AnimatedLikeButton(
                    isLiked = comment.isLiked,
                    likeCount = comment.likes,
                    onClick = onLikeClick
                )
            }

            HorizontalDivider(
                modifier = Modifier.padding(top = 8.dp),
                color = TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.light),
                thickness = TovTalkTheme.sizes.dividerThickness
            )
        }
    }
}

// ─── CreatorBadge ───────────────────────────────────────────────────────────────

@Composable
private fun CreatorBadge() {
    Surface(
        shape = RoundedCornerShape(4.dp),
        color = TovTalkTheme.colors.brand
    ) {
        Text(
            text = "\u05D9\u05D5\u05E6\u05E8",
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.onBrand,
            fontSize = 10.sp,
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp)
        )
    }
}

// ─── AnimatedLikeButton ─────────────────────────────────────────────────────────

@Composable
private fun AnimatedLikeButton(
    isLiked: Boolean,
    likeCount: Long,
    onClick: () -> Unit
) {
    var triggerAnim by remember { mutableStateOf(false) }

    val scale by animateFloatAsState(
        targetValue = if (triggerAnim) 1.3f else 1.0f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessHigh
        ),
        finishedListener = { triggerAnim = false }
    )

    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.clickable {
            triggerAnim = true
            onClick()
        }
    ) {
        Icon(
            imageVector = if (isLiked) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
            contentDescription = StringProvider.get("accessibility_like"),
            tint = if (isLiked) TovTalkTheme.colors.like else TovTalkTheme.colors.textTertiary,
            modifier = Modifier
                .size(16.dp)
                .graphicsLayer {
                    scaleX = scale
                    scaleY = scale
                }
        )
        Spacer(modifier = Modifier.width(4.dp))
        Text(
            text = "$likeCount",
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.textTertiary
        )
    }
}

// ─── ReplyItem ──────────────────────────────────────────────────────────────────

@Composable
private fun ReplyItem(
    reply: ReplyComment,
    parentComment: Comment,
    onLikeClick: () -> Unit,
    onReplyClick: () -> Unit,
    onUserClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null
) {
    val brandColor = TovTalkTheme.colors.brand
    val mentionText = remember(reply.text, brandColor) {
        reply.text?.let { text ->
            buildAnnotatedString {
                var lastIndex = 0
                MentionRegex.findAll(text).forEach { match ->
                    append(text.substring(lastIndex, match.range.first))
                    withStyle(SpanStyle(color = brandColor)) {
                        append(match.value)
                    }
                    lastIndex = match.range.last + 1
                }
                if (lastIndex < text.length) {
                    append(text.substring(lastIndex))
                }
            }
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 58.dp, end = 10.dp, top = 4.dp, bottom = 4.dp),
        verticalAlignment = Alignment.Top
    ) {
        TovTalkAvatar(
            imageUrl = reply.user?.profilePhoto,
            modifier = Modifier.clickable(onClick = onUserClick),
            size = 24.dp,
            hasStory = (reply.user?.hasActiveStory ?: false) && onStoryClick != null,
            onStoryClick = onStoryClick
        )

        Spacer(modifier = Modifier.width(8.dp))

        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = reply.user?.username ?: "",
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    modifier = Modifier.clickable(onClick = onUserClick)
                )
                Spacer(modifier = Modifier.width(6.dp))
                Text(
                    text = formatTimeAgo(reply.createdAt),
                    style = TovTalkTheme.typography.caption,
                    color = TovTalkTheme.colors.textTertiary,
                    fontSize = 10.sp
                )
            }

            mentionText?.let {
                Text(
                    text = it,
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.textPrimary,
                    maxLines = 6,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.padding(top = 2.dp)
                )
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(top = 4.dp)
            ) {
                Surface(
                    onClick = onReplyClick,
                    color = TovTalkTheme.colors.surfaceElevated
                ) {
                    Text(
                        text = "\u05D4\u05E9\u05D1",
                        style = TovTalkTheme.typography.caption,
                        color = TovTalkTheme.colors.textTertiary,
                        fontSize = 10.sp
                    )
                }

                AnimatedLikeButton(
                    isLiked = reply.isLiked,
                    likeCount = reply.likes,
                    onClick = onLikeClick
                )
            }
        }
    }
}

// ─── RepliesToggle ──────────────────────────────────────────────────────────────

@Composable
private fun RepliesToggle(
    replyCount: Long,
    isExpanded: Boolean,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .padding(start = 58.dp, top = 4.dp, bottom = 4.dp)
            .clickable(onClick = onClick),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .width(24.dp)
                .height(1.dp)
                .background(TovTalkTheme.colors.textTertiary)
        )
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = if (isExpanded) "\u05D4\u05E1\u05EA\u05E8 \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA"
            else "\u05D4\u05E6\u05D2 $replyCount \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA",
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.textTertiary
        )
    }
}

// ─── ReplyIndicator ─────────────────────────────────────────────────────────────

@Composable
private fun ReplyIndicator(
    username: String,
    onCancel: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(TovTalkTheme.colors.surfaceOverlay)
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = "\u05DE\u05E9\u05D9\u05D1 \u05DC-@$username",
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.textSecondary
        )
        IconButton(onClick = onCancel, modifier = Modifier.size(20.dp)) {
            Icon(
                imageVector = Icons.Default.Close,
                contentDescription = StringProvider.get("common_close"),
                tint = TovTalkTheme.colors.textTertiary,
                modifier = Modifier.size(16.dp)
            )
        }
    }
}

// ─── CommentInputBar ────────────────────────────────────────────────────────────

@Composable
private fun CommentInputBar(
    text: String,
    onTextChange: (String) -> Unit,
    onSend: () -> Unit,
    onEmojiTap: (String) -> Unit,
    isLoading: Boolean,
    isGuestMode: Boolean,
    onGuestAction: () -> Unit,
    currentUserAvatarUrl: String?
) {
    val quickEmojis = listOf("\uD83D\uDE02", "\uD83D\uDE0D", "\uD83D\uDC40", "\uD83D\uDD25", "\uD83D\uDC4F", "\uD83D\uDE2E")

    Surface(
        shadowElevation = 2.dp,
        color = TovTalkTheme.colors.surfaceElevated
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 8.dp)
        ) {
            // Quick emoji row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                quickEmojis.forEach { emoji ->
                    Surface(
                        onClick = {
                            if (isGuestMode) onGuestAction()
                            else onEmojiTap(emoji)
                        },
                        shape = TovTalkTheme.shapes.medium,
                        color = TovTalkTheme.colors.surfaceOverlay
                    ) {
                        Text(
                            text = emoji,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 6.dp)
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Input row
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                TovTalkAvatar(
                    imageUrl = currentUserAvatarUrl,
                    size = 28.dp
                )

                Spacer(modifier = Modifier.width(8.dp))

                if (isGuestMode) {
                    Surface(
                        modifier = Modifier
                            .weight(1f)
                            .clickable(onClick = onGuestAction),
                        shape = RoundedCornerShape(20.dp),
                        color = TovTalkTheme.colors.surfaceOverlay
                    ) {
                        Text(
                            text = "\u05D4\u05EA\u05D7\u05D1\u05E8 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D2\u05D9\u05D1",
                            color = TovTalkTheme.colors.textTertiary,
                            modifier = Modifier.padding(horizontal = 14.dp, vertical = 12.dp)
                        )
                    }
                } else {
                    OutlinedTextField(
                        value = text,
                        onValueChange = onTextChange,
                        modifier = Modifier.weight(1f),
                        placeholder = {
                            Text(
                                StringProvider.get("comments_add_hint"),
                                color = TovTalkTheme.colors.textTertiary
                            )
                        },
                        shape = RoundedCornerShape(20.dp),
                        singleLine = true,
                        enabled = !isLoading,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
                            unfocusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
                            focusedBorderColor = TovTalkTheme.colors.brand,
                            unfocusedBorderColor = TovTalkTheme.colors.border
                        )
                    )

                    Spacer(modifier = Modifier.width(8.dp))

                    Surface(
                        onClick = { if (text.isNotBlank() && !isLoading) onSend() },
                        shape = TovTalkTheme.shapes.medium,
                        color = if (text.isNotBlank() && !isLoading) TovTalkTheme.colors.brand
                        else TovTalkTheme.colors.surfaceOverlay
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            if (isLoading) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(16.dp),
                                    color = TovTalkTheme.colors.onBrand,
                                    strokeWidth = 2.dp
                                )
                            } else {
                                Text(
                                    text = "\u05E9\u05DC\u05D7",
                                    style = TovTalkTheme.typography.labelSmall,
                                    color = if (text.isNotBlank()) TovTalkTheme.colors.onBrand
                                    else TovTalkTheme.colors.textTertiary
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// ─── CommentActionSheet ─────────────────────────────────────────────────────────

@Composable
private fun CommentActionSheet(
    comment: Comment,
    isOwnComment: Boolean,
    onDismiss: () -> Unit,
    onDelete: () -> Unit
) {
    @Suppress("DEPRECATION")
    val clipboard = LocalClipboardManager.current

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.overlayDark.copy(alpha = TovTalkTheme.opacity.semiStrong))
            .clickable(onClick = onDismiss),
        contentAlignment = Alignment.BottomCenter
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(enabled = false) {},
            shape = TovTalkTheme.shapes.sheet,
            color = TovTalkTheme.colors.surfaceElevated
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 12.dp)
            ) {
                // Copy
                Surface(
                    onClick = {
                        comment.text?.let { text -> clipboard.setText(AnnotatedString(text)) }
                        onDismiss()
                    },
                    color = TovTalkTheme.colors.surfaceElevated
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(text = "\uD83D\uDCCB", modifier = Modifier.padding(end = 12.dp))
                        Text(
                            text = "\u05D4\u05E2\u05EA\u05E7 \u05D8\u05E7\u05E1\u05D8",
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textPrimary
                        )
                    }
                }

                // Report
                Surface(
                    onClick = { onDismiss() },
                    color = TovTalkTheme.colors.surfaceElevated
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(text = "\uD83D\uDEA9", modifier = Modifier.padding(end = 12.dp))
                        Text(
                            text = "\u05D3\u05D5\u05D5\u05D7",
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textPrimary
                        )
                    }
                }

                // Delete (only own)
                if (isOwnComment) {
                    Surface(
                        onClick = onDelete,
                        color = TovTalkTheme.colors.surfaceElevated
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 14.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(text = "\uD83D\uDDD1", modifier = Modifier.padding(end = 12.dp))
                            Text(
                                text = "\u05DE\u05D7\u05E7",
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.error
                            )
                        }
                    }
                }

                // Cancel
                Surface(
                    onClick = onDismiss,
                    color = TovTalkTheme.colors.surfaceElevated
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "\u05D1\u05D9\u05D8\u05D5\u05DC",
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textSecondary,
                            modifier = Modifier.fillMaxWidth(),
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}

// ─── EmptyCommentsView ──────────────────────────────────────────────────────────

@Composable
private fun EmptyCommentsView() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 12.dp),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(
                imageVector = Icons.Default.ChatBubbleOutline,
                contentDescription = null,
                modifier = Modifier.size(48.dp),
                tint = TovTalkTheme.colors.textTertiary
            )
            Spacer(modifier = Modifier.height(12.dp))
            Text(
                text = "\u05D0\u05D9\u05DF \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF",
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textSecondary
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = "\u05D4\u05D9\u05D4 \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u05DC\u05D4\u05D2\u05D9\u05D1!",
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textTertiary,
                textAlign = TextAlign.Center
            )
        }
    }
}

// ─── formatTimeAgo ──────────────────────────────────────────────────────────────

private fun formatTimeAgo(instant: Instant): String {
    val now = com.tovtalk.utils.nowInstant()
    val diffMs = now.toEpochMilliseconds() - instant.toEpochMilliseconds()
    val diffMin = diffMs / 60_000
    val diffHours = diffMs / 3600_000
    val diffDays = diffMs / 86400_000
    return when {
        diffMin < 1 -> StringProvider.get("time_now")
        diffMin < 60 -> StringProvider.get("time_minutes", diffMin.toInt())
        diffHours < 24 -> StringProvider.get("time_hours", diffHours.toInt())
        diffDays < 7 -> StringProvider.get("time_days", diffDays.toInt())
        else -> {
            val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            "${localDateTime.day}/${localDateTime.month.ordinal + 1}"
        }
    }
}
